/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import PlayerRoot from './player-root';

/**
 * Player aplikace.
 */
class Player {
  /**
   * Vytvoří instanci.
   * @param {AppManager} appManager
   * @param {ControlManager} controlManager Správce ovládání.
   * @param {Device} device Zařízení.
   * @param {FocusManager} focusManager
   * @param {Logger} logger Zaznamenávač chyb.
   * @param {NetworkListener} networkListener
   * @param {ScreenManager} screenManager Správce obrazovky.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {SetupManager} setupManager Služba pro registraci zařízení.
   * @param {VirtualKeyboardManager} virtualKeyboardManager
   * @param {VueFactory} vueFactory
   * @returns {Player} Nová instance.
   */
  constructor(
    appManager,
    controlManager,
    device,
    focusManager,
    logger,
    networkListener,
    screenManager,
    session,
    setupManager,
    virtualKeyboardManager,
    vueFactory
  ) {
    this.appManager = appManager;
    this.controlManager = controlManager;
    this.device = device;
    this.focusManager = focusManager;
    this.logger = logger;
    this.networkListener = networkListener;
    this.screenManager = screenManager;
    this.session = session;
    this.setupManager = setupManager;
    this.virtualKeyboardManager = virtualKeyboardManager;
    this.vueFactory = vueFactory;
  }

  /**
   * Vytvoří novou instanci Vue.
   * @param {HTMLElement|null|string} el DOM element pro připnutí Vue.
   * @private
   * @returns {Promise.<Vue>} Vytvořená a do DOM vložená Vue instance.
   */
  createVue(el) {
    return new Promise((resolve) => {
      const self = this;
      this.vueFactory.create({
        components: { PlayerRoot },
        el,
        template: '<PlayerRoot/>',
        // Private scope for non-reactive data and methods
        // https://github.com/vuejs/vue/issues/1988#issuecomment-163013818
        created() {
          // TODO Předávat zde celou instanci BottleJs, místo zbytečného výčtu?
          this.appManager = self.appManager;
          this.controlManager = self.controlManager;
          this.device = self.device;
          this.focusManager = self.focusManager;
          this.logger = self.logger;
          this.networkListener = self.networkListener;
          this.screenManager = self.screenManager;
          this.session = self.session;
          this.setupManager = self.setupManager;
          this.virtualKeyboardManager = self.virtualKeyboardManager;
        },
        mounted: resolve
      });
    });
  }

  /**
   * Spustí player aplikaci.
   * @param {HTMLElement|null|string} el DOM element pro připnutí aplikace.
   * @returns {Promise.<undefined>} Vyřeší se po přidání spuštěné aplikace do DOM.
   */
  start(el) {
    return new Promise((resolve) =>
      resolve(
        // Nejprve se připraví zařízení.
        this.device
          .start()
          // Až poté se začne vykreslovat GUI.
          .then(() => this.createVue(el))
      )
    );
  }
}

export default Player;
